/**
 * OnlineTab.js — Online channel analytics for the Website › Overview tab
 *
 * Self-contained: fetches its own data and owns its range picker (7 / 30 / 90 days / Month).
 * Shows: online reservations, pickup orders, waitlist signups, new customers,
 * newsletter leads, and Google Business Profile search visibility metrics.
 *
 * Month mode: compare up to 6 months side-by-side with overlaid line charts per metric.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ComposedChart,
} from 'recharts';

// ── Constants ─────────────────────────────────────────────────────────────────

const RANGES = [
  { label: '7 Days',  days: 7  },
  { label: '30 Days', days: 30 },
  { label: '90 Days', days: 90 },
  { label: 'Month',   days: -1 },
];

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const COMPARE_PALETTE = [
  '#5f799a', // brand-blue
  '#e11d48',
  '#16a34a',
  '#f97316',
  '#9333ea',
  '#0891b2',
  '#d97706',
];

const CHART_BLUE  = '#5f799a';
const CHART_GOLD  = '#C9A84C';
const CHART_GREEN = '#16a34a';
const CHART_TEAL  = '#0891b2';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDateRange(days) {
  const end   = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

function getMonthRange(year, month) {
  // month is 0-indexed (JS Date convention)
  const start = new Date(year, month, 1);
  const end   = new Date(year, month + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

function formatXAxisDate(dateObj) {
  // dateObj: { year, month, day } from GBP API
  const d = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
  return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

function formatDateOnly(dateOnly) {
  if (!dateOnly) return '';
  const [, m, d] = dateOnly.split('-');
  return `${MONTH_SHORT[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
}

function allDaysInRange(start, end) {
  const out = [];
  const cur = new Date(start);
  cur.setHours(0, 0, 0, 0);
  const endD = new Date(end);
  endD.setHours(0, 0, 0, 0);
  while (cur <= endD) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const d = String(cur.getDate()).padStart(2, '0');
    out.push(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function monthYearKey(year, month) {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

function monthLabel(year, month) {
  return `${MONTH_SHORT[month]} ${year}`;
}

/**
 * Build a 31-slot day-of-month array for a given month's raw docs.
 * Returns array of { day, online, allRes, pickup, waitlist } indexed 1–31.
 */
function buildDayData(data, year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const slots = Array.from({ length: daysInMonth }, (_, i) => ({
    day: i + 1,
    online:   0,
    allRes:   0,
    pickup:   0,
    waitlist: 0,
  }));

  // reservations: dateOnly = 'YYYY-MM-DD'
  (data.reservations || []).forEach(r => {
    if (!r.dateOnly) return;
    const parts = r.dateOnly.split('-');
    const d = parseInt(parts[2], 10);
    if (d >= 1 && d <= daysInMonth) {
      slots[d - 1].allRes++;
      if (r.source === 'widget') slots[d - 1].online++;
    }
  });

  // pickup_orders: createdAt is epoch ms
  (data.pickupOrders || []).forEach(o => {
    const ts = Number(o.createdAt);
    if (!ts) return;
    const d = new Date(ts).getDate();
    if (d >= 1 && d <= daysInMonth) slots[d - 1].pickup++;
  });

  // waitlist: createdAt is Firestore Timestamp
  (data.waitlistDocs || []).forEach(w => {
    let d = null;
    if (w.createdAt && typeof w.createdAt.toDate === 'function') {
      d = w.createdAt.toDate().getDate();
    } else if (w.createdAt && w.createdAt.seconds) {
      d = new Date(w.createdAt.seconds * 1000).getDate();
    }
    if (d && d >= 1 && d <= daysInMonth) slots[d - 1].waitlist++;
  });

  return slots;
}

// ── Shared UI ─────────────────────────────────────────────────────────────────

const KpiTile = ({ label, value, sub }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col gap-1">
    <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold text-neutral-900 leading-tight">{value ?? '—'}</p>
    {sub && <p className="text-xs text-neutral-400">{sub}</p>}
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mt-2 mb-1 px-0.5">
    {children}
  </p>
);

const EmptyState = ({ msg = 'No data for this period' }) => (
  <p className="text-sm text-neutral-400 italic py-4 text-center">{msg}</p>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4">
    {title && <p className="text-xs font-semibold text-neutral-500 mb-3">{title}</p>}
    {children}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const OnlineTab = ({ restaurantId }) => {
  const [rangeIdx, setRangeIdx] = useState(1); // default: 30 days

  // Rolling range state
  const [reservations,  setReservations]  = useState([]);
  const [pickupOrders,  setPickupOrders]  = useState([]);
  const [waitlistDocs,  setWaitlistDocs]  = useState([]);
  const [customerCount, setCustomerCount] = useState(null);
  const [nlCount,       setNlCount]       = useState(null);
  const [loading,       setLoading]       = useState(true);

  // Month mode state
  const now = new Date();
  const [monthYear,        setMonthYear]        = useState(now.getFullYear());
  const [monthMonth,       setMonthMonth]       = useState(now.getMonth()); // 0-indexed
  const [addingMonthYear,  setAddingMonthYear]  = useState(now.getFullYear());
  const [addingMonthMonth, setAddingMonthMonth] = useState(
    now.getMonth() === 0 ? 11 : now.getMonth() - 1
  );
  const [primaryMonthData, setPrimaryMonthData] = useState(null);
  const [comparePeriods,   setComparePeriods]   = useState([]);
  const [loadingMonth,     setLoadingMonth]     = useState(false);
  const [loadingCompare,   setLoadingCompare]   = useState(false);

  // GBP state
  const [gbpData,    setGbpData]    = useState(null);
  const [gbpLoading, setGbpLoading] = useState(true);

  const isMonthMode = RANGES[rangeIdx].days === -1;

  // ── Fetch a month's raw Firestore docs ───────────────────────────────────
  const fetchMonthData = useCallback(async (year, month) => {
    if (!restaurantId) return null;
    const { start, end } = getMonthRange(year, month);
    const startTs = Timestamp.fromDate(start);
    const endTs   = Timestamp.fromDate(end);

    const [resSnap, pickupSnap, waitSnap, custSnap, nlSnap] = await Promise.all([
      getDocs(query(
        collection(db, 'restaurants_data', restaurantId, 'reservations'),
        where('date', '>=', startTs),
        where('date', '<=', endTs),
      )),
      getDocs(query(
        collection(db, 'restaurants_data', restaurantId, 'pickup_orders'),
        where('createdAt', '>=', start.getTime()),
        where('createdAt', '<=', end.getTime()),
      )),
      getDocs(query(
        collection(db, 'restaurants_data', restaurantId, 'waitlist'),
        where('createdAt', '>=', startTs),
        where('createdAt', '<=', endTs),
      )),
      getDocs(query(
        collection(db, 'restaurants_data', restaurantId, 'customers'),
        where('createdAt', '>=', startTs),
        where('createdAt', '<=', endTs),
      )),
      getDocs(query(
        collection(db, 'restaurants_data', restaurantId, 'newsletterLeads'),
        where('createdAt', '>=', startTs),
        where('createdAt', '<=', endTs),
      )),
    ]);

    const reservationDocs  = resSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const pickupOrderDocs  = pickupSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const waitlistDocsList = waitSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const onlineBookings = reservationDocs.filter(r => r.source === 'widget');
    const widgetOrders   = pickupOrderDocs.filter(o => o.source === 'widget');
    const widgetRevenue  = widgetOrders.reduce((s, o) => s + (Number(o.total) || 0), 0);
    const seatedCount    = waitlistDocsList.filter(w => ['seated','completed'].includes(w.status)).length;
    const conversionPct  = waitlistDocsList.length > 0
      ? Math.round((seatedCount / waitlistDocsList.length) * 100)
      : null;

    return {
      reservations:  reservationDocs,
      pickupOrders:  pickupOrderDocs,
      waitlistDocs:  waitlistDocsList,
      customerCount: custSnap.size,
      nlCount:       nlSnap.size,
      totals: {
        onlineBookings:  onlineBookings.length,
        allReservations: reservationDocs.length,
        onlineOrders:    widgetOrders.length,
        widgetRevenue,
        waitlistSignups: waitlistDocsList.length,
        seatedPct:       conversionPct,
        newCustomers:    custSnap.size,
        newSubscribers:  nlSnap.size,
      },
    };
  }, [restaurantId]);

  // ── Load rolling range data ───────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!restaurantId || isMonthMode) return;
    setLoading(true);
    const { start, end } = getDateRange(RANGES[rangeIdx].days);
    const startTs = Timestamp.fromDate(start);

    try {
      const [resSnap, pickupSnap, waitSnap, custSnap, nlSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'restaurants_data', restaurantId, 'reservations'),
          where('date', '>=', startTs),
          where('date', '<=', Timestamp.fromDate(end)),
        )),
        getDocs(query(
          collection(db, 'restaurants_data', restaurantId, 'pickup_orders'),
          where('createdAt', '>=', start.getTime()),
          where('createdAt', '<=', end.getTime()),
        )),
        getDocs(query(
          collection(db, 'restaurants_data', restaurantId, 'waitlist'),
          where('createdAt', '>=', startTs),
        )),
        getDocs(query(
          collection(db, 'restaurants_data', restaurantId, 'customers'),
          where('createdAt', '>=', startTs),
        )),
        getDocs(query(
          collection(db, 'restaurants_data', restaurantId, 'newsletterLeads'),
          where('createdAt', '>=', startTs),
        )),
      ]);

      setReservations(resSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setPickupOrders(pickupSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setWaitlistDocs(waitSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCustomerCount(custSnap.size);
      setNlCount(nlSnap.size);
    } catch (e) {
      console.error('[OnlineTab] load error:', e);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, rangeIdx, isMonthMode]);

  useEffect(() => { load(); }, [load]);

  // ── Load primary month data when month mode is active ────────────────────
  const loadPrimaryMonth = useCallback(async () => {
    if (!restaurantId || !isMonthMode) return;
    setLoadingMonth(true);
    try {
      const data = await fetchMonthData(monthYear, monthMonth);
      setPrimaryMonthData(data);
    } catch (e) {
      console.error('[OnlineTab] month load error:', e);
    } finally {
      setLoadingMonth(false);
    }
  }, [restaurantId, isMonthMode, monthYear, monthMonth, fetchMonthData]);

  useEffect(() => { loadPrimaryMonth(); }, [loadPrimaryMonth]);

  // Clear compare periods when switching away from month mode
  useEffect(() => {
    if (!isMonthMode) {
      setComparePeriods([]);
      setPrimaryMonthData(null);
    }
  }, [isMonthMode]);

  // ── Add a comparison month ────────────────────────────────────────────────
  const addComparisonMonth = async () => {
    const key = monthYearKey(addingMonthYear, addingMonthMonth);
    // Don't add the primary month or duplicates
    const primaryKey = monthYearKey(monthYear, monthMonth);
    if (key === primaryKey) return;
    if (comparePeriods.some(p => p.key === key)) return;

    setLoadingCompare(true);
    try {
      const data = await fetchMonthData(addingMonthYear, addingMonthMonth);
      if (!data) return;
      const colorIdx = (comparePeriods.length + 1) % COMPARE_PALETTE.length;
      const dayData  = buildDayData(data, addingMonthYear, addingMonthMonth);
      setComparePeriods(prev => [
        ...prev,
        {
          key,
          label:   monthLabel(addingMonthYear, addingMonthMonth),
          color:   COMPARE_PALETTE[colorIdx],
          dayData,
          totals:  data.totals,
          year:    addingMonthYear,
          month:   addingMonthMonth,
        },
      ]);
    } catch (e) {
      console.error('[OnlineTab] compare load error:', e);
    } finally {
      setLoadingCompare(false);
    }
  };

  const removeComparePeriod = (key) => {
    setComparePeriods(prev => prev.filter(p => p.key !== key));
  };

  // ── Load GBP insights once (always 30 days) ───────────────────────────────
  useEffect(() => {
    if (!restaurantId) return;
    setGbpLoading(true);
    const getGBPInsightsFn = httpsCallable(getFunctions(), 'getGBPInsights');
    getGBPInsightsFn({ restaurantId, days: 30 })
      .then(r => setGbpData(r.data))
      .catch(() => setGbpData({ notLinked: true }))
      .finally(() => setGbpLoading(false));
  }, [restaurantId]);

  // ── Derive rolling range metrics ──────────────────────────────────────────
  const onlineBookings = reservations.filter(r => r.source === 'widget');
  const otherBookings  = reservations.filter(r => r.source !== 'widget');

  const { start: rangeStart, end: rangeEnd } = isMonthMode
    ? { start: new Date(), end: new Date() }
    : getDateRange(RANGES[rangeIdx].days);

  const allDays = isMonthMode ? [] : allDaysInRange(rangeStart, rangeEnd);
  const dayMap  = {};
  allDays.forEach(d => { dayMap[d] = { label: formatDateOnly(d), online: 0, other: 0 }; });
  reservations.forEach(r => {
    const k = r.dateOnly;
    if (k && dayMap[k]) {
      if (r.source === 'widget') dayMap[k].online++;
      else dayMap[k].other++;
    }
  });
  const bookingsByDay = Object.values(dayMap);

  const widgetOrders   = pickupOrders.filter(o => o.source === 'widget');
  const widgetRevenue  = widgetOrders.reduce((s, o) => s + (Number(o.total) || 0), 0);

  const waitlistCount  = waitlistDocs.length;
  const seatedCount    = waitlistDocs.filter(w => ['seated','completed'].includes(w.status)).length;
  const conversionPct  = waitlistCount > 0 ? Math.round((seatedCount / waitlistCount) * 100) : null;

  const fmt$ = n => n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // ── GBP processing ────────────────────────────────────────────────────────
  const gbpM = gbpData?.metrics;

  const gbpTotals = gbpM ? {
    websiteClicks: gbpM.WEBSITE_CLICKS?.reduce((s, v) => s + v.value, 0) ?? 0,
    callClicks:    gbpM.CALL_CLICKS?.reduce((s, v) => s + v.value, 0) ?? 0,
    directions:    gbpM.BUSINESS_DIRECTION_REQUESTS?.reduce((s, v) => s + v.value, 0) ?? 0,
    impressions:   [
      ...(gbpM.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH || []),
      ...(gbpM.BUSINESS_IMPRESSIONS_MOBILE_SEARCH  || []),
      ...(gbpM.BUSINESS_IMPRESSIONS_DESKTOP_MAPS   || []),
      ...(gbpM.BUSINESS_IMPRESSIONS_MOBILE_MAPS    || []),
    ].reduce((s, v) => s + v.value, 0),
  } : null;

  const gbpClicksChart = (() => {
    if (!gbpM) return [];
    const map = {};
    const add = (arr, key) => (arr || []).forEach(dv => {
      const lbl = formatXAxisDate(dv.date);
      if (!map[lbl]) map[lbl] = { label: lbl, clicks: 0, directions: 0 };
      map[lbl][key] += dv.value;
    });
    add(gbpM.WEBSITE_CLICKS,              'clicks');
    add(gbpM.BUSINESS_DIRECTION_REQUESTS, 'directions');
    return Object.values(map);
  })();

  const gbpImpressionsChart = (() => {
    if (!gbpM) return [];
    const map = {};
    const add = (arr, key) => (arr || []).forEach(dv => {
      const lbl = formatXAxisDate(dv.date);
      if (!map[lbl]) map[lbl] = { label: lbl, desktopSearch: 0, mobileSearch: 0, maps: 0 };
      map[lbl][key] += dv.value;
    });
    add(gbpM.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH, 'desktopSearch');
    add(gbpM.BUSINESS_IMPRESSIONS_MOBILE_SEARCH,  'mobileSearch');
    add(gbpM.BUSINESS_IMPRESSIONS_DESKTOP_MAPS,   'maps');
    add(gbpM.BUSINESS_IMPRESSIONS_MOBILE_MAPS,    'maps');
    return Object.values(map);
  })();

  // ── Month mode derived data ───────────────────────────────────────────────
  const primaryDayData = primaryMonthData
    ? buildDayData(primaryMonthData, monthYear, monthMonth)
    : [];

  const primaryColor = COMPARE_PALETTE[0];

  // Build per-metric overlay chart data: array of { day, [period keys]: value }
  const buildOverlayChart = (metric) => {
    const maxDays = Math.max(
      primaryDayData.length,
      ...comparePeriods.map(p => p.dayData.length)
    );
    return Array.from({ length: maxDays }, (_, i) => {
      const dayNum = i + 1;
      const entry = { day: dayNum };
      if (primaryDayData[i] != null) {
        entry[monthYearKey(monthYear, monthMonth)] = primaryDayData[i][metric];
      }
      comparePeriods.forEach(p => {
        if (p.dayData[i] != null) entry[p.key] = p.dayData[i][metric];
      });
      return entry;
    });
  };

  const allPeriods = primaryMonthData
    ? [
        {
          key:   monthYearKey(monthYear, monthMonth),
          label: monthLabel(monthYear, monthMonth),
          color: primaryColor,
          totals: primaryMonthData.totals,
        },
        ...comparePeriods,
      ]
    : [];

  const hasCompare = comparePeriods.length > 0;

  // ── Year options for selects ──────────────────────────────────────────────
  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) {
    yearOptions.push(y);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 max-w-2xl">

      {/* Range picker */}
      <div className="flex gap-2 flex-wrap">
        {RANGES.map((r, i) => (
          <button
            key={r.label}
            onClick={() => setRangeIdx(i)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
              rangeIdx === i
                ? 'bg-neutral-900 text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* ── Month mode UI ──────────────────────────────────────────────────── */}
      {isMonthMode && (
        <div className="space-y-3">

          {/* Primary month selector */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Viewing</span>
            <select
              value={monthMonth}
              onChange={e => setMonthMonth(Number(e.target.value))}
              className="text-sm border border-neutral-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-neutral-400"
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={name} value={i}>{name}</option>
              ))}
            </select>
            <select
              value={monthYear}
              onChange={e => setMonthYear(Number(e.target.value))}
              className="text-sm border border-neutral-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-neutral-400"
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Add comparison month */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Compare</span>
            <select
              value={addingMonthMonth}
              onChange={e => setAddingMonthMonth(Number(e.target.value))}
              className="text-sm border border-neutral-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-neutral-400"
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={name} value={i}>{name}</option>
              ))}
            </select>
            <select
              value={addingMonthYear}
              onChange={e => setAddingMonthYear(Number(e.target.value))}
              className="text-sm border border-neutral-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-neutral-400"
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              onClick={addComparisonMonth}
              disabled={loadingCompare || comparePeriods.length >= 6}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-neutral-900 text-white hover:bg-neutral-700 disabled:opacity-40 transition-colors"
            >
              {loadingCompare ? 'Loading…' : '+ Add'}
            </button>
          </div>

          {/* Comparison chips */}
          {comparePeriods.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {comparePeriods.map(p => (
                <span
                  key={p.key}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                  style={{ background: p.color }}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full bg-white opacity-70"
                  />
                  {p.label}
                  <button
                    onClick={() => removeComparePeriod(p.key)}
                    className="ml-1 opacity-70 hover:opacity-100 font-bold leading-none"
                    aria-label={`Remove ${p.label}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Month content */}
          {loadingMonth ? (
            <p className="text-sm text-neutral-400 text-center py-8">Loading…</p>
          ) : primaryMonthData ? (
            <>
              {/* Comparison summary table */}
              {allPeriods.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-neutral-200 rounded-xl overflow-hidden">
                    <thead>
                      <tr className="bg-neutral-50">
                        <th className="text-left px-3 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wide border-b border-neutral-200">
                          Metric
                        </th>
                        {allPeriods.map(p => (
                          <th
                            key={p.key}
                            className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wide border-b border-neutral-200 whitespace-nowrap"
                            style={{ color: p.color }}
                          >
                            {p.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'Online Bookings',    key: 'onlineBookings'  },
                        { label: 'All Reservations',   key: 'allReservations' },
                        { label: 'Online Orders',      key: 'onlineOrders'    },
                        { label: 'Waitlist Signups',   key: 'waitlistSignups' },
                        { label: 'Seated %',           key: 'seatedPct', fmt: v => v != null ? `${v}%` : '—' },
                        { label: 'New Customers',      key: 'newCustomers'    },
                        { label: 'New Subscribers',    key: 'newSubscribers'  },
                      ].map((row, ri) => (
                        <tr key={row.key} className={ri % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}>
                          <td className="px-3 py-2 text-xs font-medium text-neutral-600 border-b border-neutral-100">
                            {row.label}
                          </td>
                          {allPeriods.map(p => (
                            <td
                              key={p.key}
                              className="px-3 py-2 text-right font-semibold text-neutral-900 border-b border-neutral-100 text-sm"
                            >
                              {row.fmt
                                ? row.fmt(p.totals[row.key])
                                : (p.totals[row.key] ?? '—')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Online Reservations chart */}
              <SectionLabel>Online Reservations by Day</SectionLabel>
              <ChartCard>
                {primaryDayData.every(d => d.online === 0) && !hasCompare ? (
                  <EmptyState />
                ) : hasCompare ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={buildOverlayChart('online')}
                      margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={4} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {allPeriods.map(p => (
                        <Line
                          key={p.key}
                          type="monotone"
                          dataKey={p.key}
                          name={p.label}
                          stroke={p.color}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={primaryDayData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="onlineGradMonth" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={CHART_BLUE} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={CHART_BLUE} stopOpacity={0}   />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={4} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <Area
                        type="monotone"
                        dataKey="online"
                        name="Online"
                        stroke={CHART_BLUE}
                        fill="url(#onlineGradMonth)"
                        strokeWidth={2}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              {/* Pickup Orders chart */}
              <SectionLabel>Pickup Orders by Day</SectionLabel>
              <ChartCard>
                {primaryDayData.every(d => d.pickup === 0) && !hasCompare ? (
                  <EmptyState />
                ) : hasCompare ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={buildOverlayChart('pickup')}
                      margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={4} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {allPeriods.map(p => (
                        <Line
                          key={p.key}
                          type="monotone"
                          dataKey={p.key}
                          name={p.label}
                          stroke={p.color}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={primaryDayData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="pickupGradMonth" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={CHART_GOLD} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={CHART_GOLD} stopOpacity={0}   />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={4} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <Area
                        type="monotone"
                        dataKey="pickup"
                        name="Orders"
                        stroke={CHART_GOLD}
                        fill="url(#pickupGradMonth)"
                        strokeWidth={2}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              {/* Waitlist Signups chart */}
              <SectionLabel>Waitlist Signups by Day</SectionLabel>
              <ChartCard>
                {primaryDayData.every(d => d.waitlist === 0) && !hasCompare ? (
                  <EmptyState />
                ) : hasCompare ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={buildOverlayChart('waitlist')}
                      margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={4} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {allPeriods.map(p => (
                        <Line
                          key={p.key}
                          type="monotone"
                          dataKey={p.key}
                          name={p.label}
                          stroke={p.color}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={primaryDayData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="waitlistGradMonth" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={CHART_TEAL} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={CHART_TEAL} stopOpacity={0}   />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={4} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <Area
                        type="monotone"
                        dataKey="waitlist"
                        name="Signups"
                        stroke={CHART_TEAL}
                        fill="url(#waitlistGradMonth)"
                        strokeWidth={2}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </>
          ) : null}
        </div>
      )}

      {/* ── Rolling range UI ───────────────────────────────────────────────── */}
      {!isMonthMode && (
        loading ? (
          <p className="text-sm text-neutral-400 text-center py-8">Loading…</p>
        ) : (
          <>
            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KpiTile
                label="Online Bookings"
                value={onlineBookings.length}
                sub={`of ${reservations.length} total`}
              />
              <KpiTile
                label="Online Orders"
                value={widgetOrders.length}
                sub={widgetRevenue > 0 ? fmt$(widgetRevenue) : undefined}
              />
              <KpiTile
                label="Waitlist Signups"
                value={waitlistCount}
                sub={conversionPct != null ? `${conversionPct}% seated` : undefined}
              />
              <KpiTile
                label="New Customers"
                value={customerCount ?? '—'}
              />
            </div>

            {/* Reservations chart */}
            <SectionLabel>Online Reservations</SectionLabel>
            <ChartCard>
              {bookingsByDay.every(d => d.online === 0 && d.other === 0) ? (
                <EmptyState />
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={bookingsByDay} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="onlineGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={CHART_BLUE} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={CHART_BLUE} stopOpacity={0}   />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <Area
                        type="monotone"
                        dataKey="online"
                        name="Online"
                        stroke={CHART_BLUE}
                        fill="url(#onlineGrad)"
                        strokeWidth={2}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="text-center">
                      <p className="text-xs text-neutral-400 uppercase tracking-wide">Via Website</p>
                      <p className="text-xl font-bold text-neutral-900">{onlineBookings.length}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-neutral-400 uppercase tracking-wide">In-House</p>
                      <p className="text-xl font-bold text-neutral-900">{otherBookings.length}</p>
                    </div>
                  </div>
                </>
              )}
            </ChartCard>

            {/* Waitlist */}
            <SectionLabel>Waitlist</SectionLabel>
            <ChartCard>
              {waitlistCount === 0 ? (
                <EmptyState />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center">
                    <p className="text-xs text-neutral-400 uppercase tracking-wide">Signups</p>
                    <p className="text-2xl font-bold text-neutral-900">{waitlistCount}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-neutral-400 uppercase tracking-wide">Seated</p>
                    <p className="text-2xl font-bold text-neutral-900">
                      {conversionPct != null ? `${conversionPct}%` : '—'}
                    </p>
                  </div>
                </div>
              )}
            </ChartCard>

            {/* Newsletter */}
            <SectionLabel>Newsletter</SectionLabel>
            <ChartCard>
              {nlCount === 0 ? (
                <EmptyState msg="No new subscribers this period" />
              ) : (
                <div className="text-center py-1">
                  <p className="text-xs text-neutral-400 uppercase tracking-wide">New Subscribers</p>
                  <p className="text-2xl font-bold text-neutral-900">{nlCount}</p>
                </div>
              )}
            </ChartCard>
          </>
        )
      )}

      {/* GBP section — always shows last 30 days, independent of range picker */}
      <SectionLabel>Google Visibility (Last 30 Days)</SectionLabel>

      {gbpLoading ? (
        <ChartCard>
          <p className="text-sm text-neutral-400 text-center py-4">Loading Google data…</p>
        </ChartCard>
      ) : gbpData?.notLinked ? (
        <ChartCard>
          <p className="text-sm text-neutral-400 italic text-center py-4">
            Connect Google Business Profile in the Google tab to see search visibility data.
          </p>
        </ChartCard>
      ) : gbpTotals ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiTile label="Website Clicks"     value={gbpTotals.websiteClicks.toLocaleString()} />
            <KpiTile label="Call Clicks"        value={gbpTotals.callClicks.toLocaleString()} />
            <KpiTile label="Direction Requests" value={gbpTotals.directions.toLocaleString()} />
            <KpiTile label="Search Impressions" value={gbpTotals.impressions.toLocaleString()} />
          </div>

          {gbpClicksChart.length > 0 && (
            <ChartCard title="Clicks & Direction Requests">
              <ResponsiveContainer width="100%" height={180}>
                <ComposedChart data={gbpClicksChart} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="clicksGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={CHART_BLUE}  stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_BLUE}  stopOpacity={0}   />
                    </linearGradient>
                    <linearGradient id="directionsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={CHART_GREEN} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_GREEN} stopOpacity={0}   />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  <Area type="monotone" dataKey="clicks"     name="Website Clicks"      stroke={CHART_BLUE}  fill="url(#clicksGrad)"     strokeWidth={2} dot={false} />
                  <Area type="monotone" dataKey="directions" name="Direction Requests"  stroke={CHART_GREEN} fill="url(#directionsGrad)" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {gbpImpressionsChart.length > 0 && (
            <ChartCard title="Search Impressions Breakdown">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={gbpImpressionsChart} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  <Bar dataKey="desktopSearch" name="Desktop Search" stackId="a" fill={CHART_BLUE} />
                  <Bar dataKey="mobileSearch"  name="Mobile Search"  stackId="a" fill={CHART_GOLD} />
                  <Bar dataKey="maps"          name="Maps"           stackId="a" fill={CHART_TEAL} radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex gap-4 mt-2 justify-center flex-wrap">
                {[
                  { color: CHART_BLUE,  label: 'Desktop Search' },
                  { color: CHART_GOLD,  label: 'Mobile Search' },
                  { color: CHART_TEAL,  label: 'Maps' },
                ].map(({ color, label }) => (
                  <span key={label} className="flex items-center gap-1 text-xs text-neutral-500">
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color }} />
                    {label}
                  </span>
                ))}
              </div>
            </ChartCard>
          )}
        </>
      ) : null}
    </div>
  );
};

export default OnlineTab;
